'use client';

import { useState, useEffect, useRef, useCallback, useMemo, startTransition, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Menu, X, Sun, Moon, Home, Calendar, Bot, Brain, BarChart3,
    FileCheck, Radio, Monitor, ChevronDown, Database,
    Users, PenTool, Presentation, Box, Image, Palette,
    Compass, Landmark, ClipboardList, DollarSign, CreditCard, Accessibility,
    Server, RefreshCw, TrendingUp, Building2, MessageSquare, ShieldAlert
} from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTheme } from './ThemeContext';
import { PipelineInbox } from './PipelineInbox';
import { openPopupRAGChat } from './PopupRAGChat';

interface NavItem {
    href: string;
    label: string;
    icon: React.ReactNode;
    badge?: string;
}

const AI_TOOLS = [
    { href: '/agent', label: 'SnapAgent', icon: <Bot className="w-3.5 h-3.5" />, color: 'from-blue-500 to-indigo-600', desc: 'AI 통합 파이프라인', status: 'active' as const },
    { href: '/live', label: 'NAS', icon: <Database className="w-3.5 h-3.5" />, color: 'from-slate-500 to-gray-700', desc: '도면/내역 서버 연동', status: 'active' as const },
    { href: '/flow', label: 'CoWork', icon: <Users className="w-3.5 h-3.5" />, color: 'from-violet-500 to-purple-600', desc: '협업 · 프로젝트 관리', status: 'active' as const },
    { href: '/cad-agent', label: 'CAD', icon: <PenTool className="w-3.5 h-3.5" />, color: 'from-emerald-500 to-cyan-600', desc: 'DWG 분석 · CXD 변환', status: 'active' as const },
    { href: '/ppt-agent', label: 'PPT', icon: <Presentation className="w-3.5 h-3.5" />, color: 'from-blue-500 to-indigo-600', desc: 'PPT 자동 생성', status: 'planned' as const },
    { href: '/skp-agent', label: 'SKP', icon: <Box className="w-3.5 h-3.5" />, color: 'from-sky-500 to-blue-600', desc: 'SketchUp 3D 분석', status: 'planned' as const },
    { href: '/ren-agent', label: 'REN', icon: <Image className="w-3.5 h-3.5" />, color: 'from-purple-500 to-pink-600', desc: 'AI 렌더링', status: 'planned' as const },
    { href: '/color-agent', label: 'COLOR', icon: <Palette className="w-3.5 h-3.5" />, color: 'from-pink-500 to-rose-600', desc: '색채 분석 · NCS', status: 'planned' as const },
    { href: '/design-agent', label: 'DESIGN', icon: <Compass className="w-3.5 h-3.5" />, color: 'from-teal-500 to-emerald-600', desc: '설계 기획 · 프로그램', status: 'dev' as const },
    { href: '/btl-agent', label: 'BTL', icon: <Landmark className="w-3.5 h-3.5" />, color: 'from-amber-500 to-orange-600', desc: 'BTL 입찰 자동화', status: 'dev' as const },
    { href: '/boq-agent', label: 'BOQ', icon: <ClipboardList className="w-3.5 h-3.5" />, color: 'from-cyan-500 to-blue-600', desc: '수량 산출', status: 'planned' as const },
    { href: '/cost-agent', label: 'COST', icon: <DollarSign className="w-3.5 h-3.5" />, color: 'from-emerald-500 to-green-600', desc: '공사비 예측', status: 'dev' as const },
    { href: '/pay-agent', label: 'PAY', icon: <CreditCard className="w-3.5 h-3.5" />, color: 'from-violet-500 to-purple-600', desc: '기성 관리 (EVM)', status: 'active' as const },
    { href: '/bf-agent', label: 'BF', icon: <Accessibility className="w-3.5 h-3.5" />, color: 'from-sky-500 to-cyan-600', desc: 'BF 인증 검토', status: 'planned' as const },
];

// Routes excluded from AppNavbar (have their own nav)
const SELF_NAV_ROUTES = ['/live', '/cowork'];

export const AppNavbar = memo(function AppNavbar() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [toolsOpen, setToolsOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const moreRef = useRef<HTMLDivElement>(null);
    const [mounted, setMounted] = useState(false);
    const toolsRef = useRef<HTMLDivElement>(null);
    const pathname = usePathname();
    const { theme, toggleTheme } = useTheme();
    const isDark = theme === 'dark';

    useEffect(() => { setMounted(true); }, []);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // 초기 진입부 스크롤 상태 동기화
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => { startTransition(() => { setIsOpen(false); setToolsOpen(false); setMoreOpen(false); }); }, [pathname]);
    useEffect(() => { document.body.style.overflow = isOpen ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [isOpen]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) setToolsOpen(false);
            if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const isActive = useCallback((href: string) => {
        if (href === '/') return pathname === '/';
        return pathname.startsWith(href);
    }, [pathname]);

    const closeDesktopDropdowns = useCallback(() => {
        setMoreOpen(false);
        setToolsOpen(false);
    }, []);

    const toggleMore = useCallback(() => {
        setMoreOpen((open) => !open);
        setToolsOpen(false);
    }, []);

    const toggleTools = useCallback(() => {
        setToolsOpen((open) => !open);
        setMoreOpen(false);
    }, []);

    const statusBadge = (s: 'active' | 'dev' | 'planned') => {
        if (s === 'active') return <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />;
        if (s === 'dev') return <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />;
        return <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />;
    };

    // 홈('/')에서는 전역 Navbar가 표시되므로 AppNavbar를 숨김
    // /live, /cowork는 자체 fixed nav를 갖고 있으므로 AppNavbar 제외
    if (pathname === '/' || SELF_NAV_ROUTES.some(r => pathname?.startsWith(r))) return null;

    // 하이드레이션 오류 방지를 위해 mounted 직전에는 Navbar의 틀만 맞춘 빈 모습 렌더링 또는 초기값 유지
    if (!mounted) {
        return <nav className="fixed top-0 left-0 right-0 z-50 h-14 sm:h-16 bg-transparent" />;
    }

    const NAV_ITEMS: NavItem[] = [
        { href: '/', label: '홈', icon: <Home className="w-4 h-4" /> },
        { href: '/scheduler', label: '스케줄러', icon: <Calendar className="w-4 h-4" /> },
        { href: '/flow', label: '플로우', icon: <Users className="w-4 h-4" /> },
        { href: '/agent', label: 'SnapAgent', icon: <Bot className="w-4 h-4" /> },
        { href: '/ai-hub', label: 'AI Hub', icon: <Brain className="w-4 h-4" /> },
        { href: '/live', label: 'LIVE', icon: <Radio className="w-4 h-4" />, badge: '●' },
    ];

    const MORE_NAV_ITEMS: NavItem[] = [
        { href: '/audit-dashboard', label: 'Audit Dashboard', icon: <ShieldAlert className="w-4 h-4 text-emerald-500" /> },
        { href: '/analytics', label: '분석', icon: <BarChart3 className="w-4 h-4" /> },
        { href: '/approval', label: '결재', icon: <FileCheck className="w-4 h-4" /> },
        { href: '/grid', label: '그리드', icon: <Monitor className="w-4 h-4" /> },
        { href: '/ir', label: 'IR', icon: <TrendingUp className="w-4 h-4" /> },
        { href: '/case/ptk', label: '실증', icon: <Building2 className="w-4 h-4" /> },
        { href: '/ontology', label: '온톨로지', icon: <Brain className="w-4 h-4" /> },
        { href: '/admin/provision', label: 'NAS관리', icon: <Server className="w-4 h-4" /> },
        { href: '/admin/cache', label: '캐싱', icon: <Database className="w-4 h-4" /> },
    ];

    const isAgentPage = AI_TOOLS.some(t => pathname.startsWith(t.href));

    return (
        <>
            <nav
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
                        ? isDark
                            ? 'bg-gray-950/90 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20'
                            : 'bg-white/92 backdrop-blur-xl border-b border-black/5 shadow-sm'
                        : isDark
                            ? 'bg-gray-950/60 backdrop-blur-md'
                            : 'bg-white/80 backdrop-blur-md'
                    }`}
            >
                <div className="w-full px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
                    {/* Logo */}
                    <Link href="/" className="flex items-center gap-2 group shrink-0">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs sm:text-sm group-hover:scale-110 transition-transform">
                            D
                        </div>
                        <span className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            SnapGRID
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-0.5">
                        {NAV_ITEMS.map((item) => (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={`relative whitespace-nowrap flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${isActive(item.href)
                                        ? isDark
                                            ? 'bg-white/10 text-white font-semibold'
                                            : 'bg-blue-50 text-blue-700 font-semibold'
                                        : isDark
                                            ? 'text-gray-400 hover:text-white hover:bg-white/5'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    } ${item.badge ? 'text-red-400' : ''}`}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                                {item.badge && (
                                    <span className="text-red-500 text-[8px] animate-pulse">{item.badge}</span>
                                )}
                                {isActive(item.href) && (
                                    <motion.div
                                        layoutId="nav-ind"
                                        className={`absolute bottom-0 left-2 right-2 h-0.5 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                            </Link>
                        ))}

                        {/* More Nav Dropdown */}
                        <div className="relative" ref={moreRef}>
                            <button
                                onClick={() => startTransition(toggleMore)}
                                className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-all duration-200 cursor-pointer ${
                                    moreOpen || MORE_NAV_ITEMS.some(m => isActive(m.href))
                                        ? isDark ? 'bg-white/10 text-white font-semibold' : 'bg-gray-100 text-gray-900 font-semibold'
                                        : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                            >
                                <Menu className="w-4 h-4" />
                                <span>더보기</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
                            </button>

                            <AnimatePresence>
                                {moreOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                                        transition={{ duration: 0.15 }}
                                        className={`absolute right-0 top-full mt-1.5 w-52 rounded-xl border shadow-xl overflow-hidden z-[100] ${isDark ? 'bg-gray-950/95 backdrop-blur-xl border-white/15' : 'bg-white border-gray-200'}`}
                                    >
                                        {MORE_NAV_ITEMS.map((item) => (
                                            <Link key={item.href} href={item.href} onClick={closeDesktopDropdowns}
                                                className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-all ${
                                                    isActive(item.href)
                                                        ? isDark ? 'bg-white/10 text-white font-semibold' : 'bg-gray-100 text-gray-900 font-semibold'
                                                        : isDark ? 'text-gray-300 hover:bg-white/[0.04]' : 'text-gray-700 hover:bg-gray-50'
                                                }`}>
                                                <span className="opacity-70">{item.icon}</span>
                                                <span>{item.label}</span>
                                                {isActive(item.href) && (
                                                    <span className={`ml-auto w-1.5 h-1.5 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`} />
                                                )}
                                            </Link>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* AI Tools Dropdown */}
                        <div className="relative" ref={toolsRef}>
                            <button
                                onClick={() => startTransition(toggleTools)}
                                className={`relative whitespace-nowrap flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-all duration-200 cursor-pointer ${
                                    isAgentPage || toolsOpen
                                        ? isDark ? 'bg-violet-500/15 text-violet-300 font-semibold' : 'bg-violet-50 text-violet-700 font-semibold'
                                        : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                            >
                                <span className="text-sm"><Bot className="w-4 h-4" /></span>
                                <span>AI Tools</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${toolsOpen ? 'rotate-180' : ''}`} />
                                {isAgentPage && !toolsOpen && (
                                    <motion.div layoutId="agent-ind"
                                        className={`absolute bottom-0 left-2 right-2 h-0.5 rounded-full ${isDark ? 'bg-violet-400' : 'bg-violet-600'}`}
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }} />
                                )}
                            </button>

                            <AnimatePresence>
                                {toolsOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                                        transition={{ duration: 0.15 }}
                                        className={`absolute right-0 top-full mt-1.5 w-80 rounded-2xl border shadow-2xl overflow-hidden z-[100] ${isDark ? 'bg-gray-950/95 backdrop-blur-xl border-white/15' : 'bg-white border-gray-200 shadow-xl'}`}
                                    >
                                        <div className={`px-3 py-2 border-b text-[10px] font-bold ${isDark ? 'border-white/10 text-gray-500 bg-white/[0.02]' : 'border-gray-100 text-gray-400 bg-gray-50'}`}>
                                            SnapGRID AI Agent Tools — 12개
                                        </div>
                                        <div className="max-h-[420px] overflow-y-auto p-1.5 space-y-0.5">
                                            {AI_TOOLS.map(tool => (
                                                <Link key={tool.href} href={tool.href} onClick={closeDesktopDropdowns}
                                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                                                        pathname.startsWith(tool.href)
                                                            ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'
                                                            : isDark ? 'text-gray-300 hover:bg-white/[0.04]' : 'text-gray-700 hover:bg-gray-50'
                                                    }`}>
                                                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center text-white shrink-0`}>
                                                        {tool.icon}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-[11px] font-bold flex items-center gap-1.5">
                                                            {tool.label}
                                                            {statusBadge(tool.status)}
                                                        </div>
                                                        <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{tool.desc}</div>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Desktop Actions */}
                    <div className="hidden lg:flex items-center gap-2 shrink-0">
                        <PipelineInbox />
                        {/* RAG 챗 팝업 버튼 */}
                        <button
                            onClick={openPopupRAGChat}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                                isDark
                                    ? 'bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/20 text-cyan-400'
                                    : 'bg-cyan-50 border border-cyan-200 hover:bg-cyan-100 text-cyan-600'
                            }`}
                            title="RAG AI 챗봇 열기"
                        >
                            <MessageSquare className="w-4 h-4" />
                        </button>
                        {/* Cmd+K hint */}
                        <button
                            onClick={() => { const e = new KeyboardEvent('keydown', { key: 'k', metaKey: true }); window.dispatchEvent(e); }}
                            className={`hidden xl:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] transition cursor-pointer ${isDark ? 'bg-white/5 border border-white/10 text-gray-500 hover:text-gray-300' : 'bg-gray-100 border border-gray-200 text-gray-400 hover:text-gray-600'}`}
                            title="빠른 검색 (⌘K)"
                        >
                            <span>⌘K</span>
                        </button>
                        <button
                            onClick={toggleTheme}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer ${isDark
                                    ? 'bg-white/5 border border-white/10 hover:bg-white/10 text-yellow-400'
                                    : 'bg-gray-100 border border-gray-200 hover:bg-gray-200 text-blue-600'
                                }`}
                            title={isDark ? 'Light mode' : 'Dark mode'}
                        >
                            {mounted ? (isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />) : <Sun className="w-4 h-4 opacity-0" />}
                        </button>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="flex items-center gap-1 lg:hidden">
                        <PipelineInbox />
                        <button
                            onClick={() => startTransition(() => setIsOpen(!isOpen))}
                        className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${isDark
                                ? 'bg-white/5 text-white hover:bg-white/10'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        aria-label="메뉴 토글"
                    >
                        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setIsOpen(false)} />
                        <motion.div
                            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            className={`fixed top-0 right-0 bottom-0 z-50 w-72 sm:w-80 overflow-y-auto shadow-2xl lg:hidden ${isDark ? 'bg-gray-950 border-l border-white/10' : 'bg-white border-l border-gray-200'}`}
                        >
                            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                <span className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>메뉴</span>
                                <button onClick={() => setIsOpen(false)} className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-3 space-y-1">
                                {NAV_ITEMS.map((item) => (
                                    <div key={item.href}>
                                        <Link href={item.href} onClick={() => setIsOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive(item.href) ? isDark ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-50 text-blue-700' : isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-50'}`}>
                                            <span className={isActive(item.href) ? '' : 'opacity-60'}>{item.icon}</span>
                                            <span>{item.label}</span>
                                            {item.badge && <span className="ml-auto text-red-500 text-xs animate-pulse font-bold">{item.badge}</span>}
                                        </Link>
                                    </div>
                                ))}
                                <div className={`border-t my-2 ${isDark ? 'border-white/10' : 'border-gray-100'}`} />
                                {MORE_NAV_ITEMS.map((item) => (
                                    <div key={item.href}>
                                        <Link href={item.href} onClick={() => setIsOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive(item.href) ? isDark ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-50 text-blue-700' : isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-50'}`}>
                                            <span className={isActive(item.href) ? '' : 'opacity-60'}>{item.icon}</span>
                                            <span>{item.label}</span>
                                        </Link>
                                    </div>
                                ))}
                            </div>

                            {/* AI Tools Section */}
                            <div className={`px-3 pt-2 pb-1 ${isDark ? 'border-t border-white/10' : 'border-t border-gray-100'}`}>
                                <div className={`text-[10px] font-bold px-4 py-2 flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}><Bot className="w-3 h-3" /> AI Tools</div>
                                <div className="space-y-0.5">
                                    {AI_TOOLS.map((tool) => (
                                        <div key={tool.href}>
                                            <Link href={tool.href} onClick={() => setIsOpen(false)}
                                                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${pathname.startsWith(tool.href) ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-50'}`}>
                                                <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center text-white shrink-0`}>
                                                    {tool.icon}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs font-semibold flex items-center gap-1.5">{tool.label} {statusBadge(tool.status)}</div>
                                                    <div className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{tool.desc}</div>
                                                </div>
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={`p-4 mt-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                <button onClick={toggleTheme}
                                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${isDark ? 'bg-white/5 border border-white/10 text-yellow-400' : 'bg-gray-100 border border-gray-200 text-blue-600'}`}>
                                    {mounted ? (isDark ? <><Sun className="w-4 h-4" /> 라이트</> : <><Moon className="w-4 h-4" /> 다크</>) : <><Sun className="w-4 h-4 opacity-0" /> 전환</>}
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
});
